import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { getUsersWithoutMatchesToday } from "./apis/get";

const DEFAULT_LIMIT = "20";
const getTodayDateInput = () => {
  const date = new Date();
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 10);
};

const getDefaultFilters = () => ({
  search: "",
  assigned_to: "",
  gender: "",
  city: "",
  state: "",
  date: getTodayDateInput(),
  sort_by: "last_match_published_at",
  sort_order: "asc",
  limit: DEFAULT_LIMIT,
  offset: "0",
});

const GENDER_OPTIONS = [
  { value: "", label: "All genders" },
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

const TABLE_COLUMNS = [
  { key: "name", label: "Name", sortable: true },
  { key: "gender", label: "Gender", sortable: false },
  { key: "phone_number", label: "Phone", sortable: false },
  { key: "city", label: "City", sortable: true },
  { key: "state", label: "State", sortable: true },
  { key: "occupation", label: "Occupation", sortable: false },
  { key: "assigned_to", label: "Assigned To", sortable: true },
  { key: "total_matches_published", label: "Published", sortable: true },
  { key: "total_matches_unpublished", label: "Unpublished", sortable: true },
  { key: "last_match_published_at", label: "Last Published", sortable: true },
  { key: "last_active", label: "Last Active", sortable: true },
];

const inputClassName =
  "w-full rounded-xl border border-slate-700 bg-slate-950/90 px-4 py-3 text-sm text-slate-100 outline-none transition placeholder:text-slate-500 focus:border-sky-400 focus:ring-2 focus:ring-sky-500/20";
const selectClassName =
  "w-full rounded-xl border border-slate-700 bg-slate-950/90 px-4 py-3 text-sm text-slate-100 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-500/20";

const toDisplay = (value, fallback = "--") => {
  if (value === undefined || value === null || value === "") return fallback;
  return String(value);
};

const formatDateTime = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const normalizeResponse = (response) => {
  const root = response || {};
  const data = root?.data || {};
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const pagination = data?.pagination || {};

  return {
    rows,
    date: data?.date || "",
    pagination: {
      total: Number(pagination?.total || 0),
      count: Number(pagination?.count || rows.length || 0),
      limit: Number(pagination?.limit || DEFAULT_LIMIT),
      offset: Number(pagination?.offset || 0),
      has_more: Boolean(pagination?.has_more),
    },
  };
};

const buildRequestFilters = (filters) =>
  Object.entries(filters).reduce((accumulator, [key, value]) => {
    if (value !== "" && value !== null && value !== undefined) {
      accumulator[key] = value;
    }
    return accumulator;
  }, {});

const UserDetailsLink = ({ userId }) => (
  <Link
    to={`/users/user/${userId}`}
    state={{ from: "users-without-matches-today" }}
    className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-sky-500/30 bg-sky-500/10 px-2.5 py-1.5 text-[11px] font-semibold text-sky-200 transition hover:border-sky-400 hover:text-white"
  >
    View details
    <ExternalLink size={12} />
  </Link>
);

const UsersWithoutMatchesToday = () => {
  const [filters, setFilters] = useState(getDefaultFilters);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    count: 0,
    limit: Number(DEFAULT_LIMIT),
    offset: 0,
    has_more: false,
  });
  const [resultDate, setResultDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((pagination.total || 0) / Number(filters.limit || DEFAULT_LIMIT))),
    [filters.limit, pagination.total],
  );
  const currentPage = useMemo(
    () => Math.floor(Number(filters.offset || 0) / Number(filters.limit || DEFAULT_LIMIT)) + 1,
    [filters.limit, filters.offset],
  );

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getUsersWithoutMatchesToday(buildRequestFilters(filters));
      const normalized = normalizeResponse(response);
      setRows(normalized.rows);
      setPagination(normalized.pagination);
      setResultDate(normalized.date);
    } catch (fetchError) {
      setError(fetchError?.message || "Failed to fetch users without matches today");
      setRows([]);
      setPagination((prev) => ({ ...prev, total: 0, count: 0, has_more: false }));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      offset: key === "offset" ? value : "0",
    }));
  };

  const handleSort = (column) => {
    if (!column.sortable) return;
    setFilters((prev) => ({
      ...prev,
      sort_by: column.key,
      sort_order:
        prev.sort_by === column.key && prev.sort_order === "asc" ? "desc" : "asc",
      offset: "0",
    }));
  };

  const changePage = (direction) => {
    const limit = Number(filters.limit || DEFAULT_LIMIT);
    const nextOffset = Math.max(0, Number(filters.offset || 0) + direction * limit);
    setFilters((prev) => ({ ...prev, offset: String(nextOffset) }));
  };

  const clearFilters = () => setFilters(getDefaultFilters());

  return (
    <main className="min-h-screen bg-slate-950 px-4 py-6 text-slate-100 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1500px] space-y-5">
        <header className="flex flex-col gap-4 border-b border-slate-800 pb-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-sky-300">
              Match Delivery
            </p>
            <h1 className="mt-2 text-2xl font-bold text-white sm:text-3xl">
              Users Without Matches Today
            </h1>
            <p className="mt-2 text-sm text-slate-400">
              Active profile users with no published match on {resultDate || "today"}.
            </p>
          </div>
          <button
            type="button"
            onClick={fetchRows}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-700 px-4 py-3 text-sm font-semibold text-slate-200 transition hover:border-sky-500 hover:text-white disabled:opacity-60"
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </header>

        <section className="grid gap-3 rounded-xl border border-slate-800 bg-slate-900/50 p-4 md:grid-cols-2 xl:grid-cols-7">
          <label className="xl:col-span-2">
            <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Search
            </span>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-3.5 text-slate-500" size={16} />
              <input
                className={`${inputClassName} pl-10`}
                value={filters.search}
                onChange={(event) => updateFilter("search", event.target.value)}
                placeholder="Name, UID, matrimonial ID or phone"
              />
            </div>
          </label>

          <label>
            <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Date
            </span>
            <input
              className={inputClassName}
              type="date"
              value={filters.date}
              onChange={(event) => updateFilter("date", event.target.value)}
            />
          </label>

          <label>
            <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Gender
            </span>
            <select
              className={selectClassName}
              value={filters.gender}
              onChange={(event) => updateFilter("gender", event.target.value)}
            >
              {GENDER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              City
            </span>
            <input
              className={inputClassName}
              value={filters.city}
              onChange={(event) => updateFilter("city", event.target.value)}
              placeholder="City"
            />
          </label>

          <label>
            <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              State
            </span>
            <input
              className={inputClassName}
              value={filters.state}
              onChange={(event) => updateFilter("state", event.target.value)}
              placeholder="State"
            />
          </label>

          <label>
            <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Assigned
            </span>
            <input
              className={inputClassName}
              value={filters.assigned_to}
              onChange={(event) => updateFilter("assigned_to", event.target.value)}
              placeholder="Admin or empty"
            />
          </label>

          <div className="flex items-end gap-2 md:col-span-2 xl:col-span-7">
            <label className="w-32">
              <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Limit
              </span>
              <select
                className={selectClassName}
                value={filters.limit}
                onChange={(event) => updateFilter("limit", event.target.value)}
              >
                {["10", "20", "50", "100"].map((limit) => (
                  <option key={limit} value={limit}>
                    {limit}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-700 px-4 py-3 text-sm font-semibold text-slate-300 transition hover:border-slate-500 hover:text-white"
            >
              <X size={16} />
              Clear
            </button>
          </div>
        </section>

        <section className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900/40">
          <div className="flex flex-col gap-3 border-b border-slate-800 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-white">
                {pagination.total.toLocaleString()} users pending matches
              </p>
              <p className="text-xs text-slate-500">
                Page {currentPage} of {totalPages}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => changePage(-1)}
                disabled={loading || Number(filters.offset || 0) === 0}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-300 transition hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft size={16} />
                Prev
              </button>
              <button
                type="button"
                onClick={() => changePage(1)}
                disabled={loading || !pagination.has_more}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-300 transition hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          {error ? (
            <div className="flex min-h-[260px] flex-col items-center justify-center gap-3 p-8 text-center">
              <AlertCircle className="text-rose-300" size={22} />
              <p className="font-semibold text-white">{error}</p>
            </div>
          ) : loading ? (
            <div className="space-y-3 p-5">
              {Array.from({ length: 8 }).map((_, index) => (
                <div key={index} className="h-12 animate-pulse rounded-xl bg-slate-800/80" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="flex min-h-[260px] flex-col items-center justify-center gap-3 p-8 text-center">
              <AlertCircle className="text-slate-400" size={22} />
              <p className="font-semibold text-white">No pending users found</p>
              <p className="text-sm text-slate-500">
                Everyone in this filter has received a published match for the selected date.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-800 text-sm">
                <thead className="bg-slate-950/80">
                  <tr>
                    {TABLE_COLUMNS.map((column) => (
                      <th
                        key={column.key}
                        scope="col"
                        className={`whitespace-nowrap px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.18em] text-slate-500 ${
                          column.sortable ? "cursor-pointer select-none hover:text-sky-300" : ""
                        }`}
                        onClick={() => handleSort(column)}
                      >
                        <span className="inline-flex items-center gap-1">
                          {column.label}
                          {filters.sort_by === column.key ? (
                            <span className="text-sky-300">
                              {filters.sort_order === "asc" ? "ASC" : "DESC"}
                            </span>
                          ) : null}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/80">
                  {rows.map((row) => (
                    <tr key={row.user_id} className="transition hover:bg-slate-800/35">
                      <td className="whitespace-nowrap px-4 py-3">
                        <div className="font-semibold text-white">{toDisplay(row.name)}</div>
                        <div className="mt-1 font-mono text-xs text-sky-200">
                          {toDisplay(row.user_id)}
                        </div>
                        <UserDetailsLink userId={row.user_id} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-300">
                        {toDisplay(row.gender)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-300">
                        {toDisplay(row.phone_number)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-300">
                        {toDisplay(row.city)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-300">
                        {toDisplay(row.state)}
                      </td>
                      <td className="min-w-[180px] px-4 py-3 text-slate-300">
                        {toDisplay(row.occupation)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-300">
                        {toDisplay(row.assigned_to)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-300">
                        {toDisplay(row.total_matches_published, "0")}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-300">
                        {toDisplay(row.total_matches_unpublished, "0")}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-300">
                        {formatDateTime(row.last_match_published_at)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-300">
                        {formatDateTime(row.last_active)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default UsersWithoutMatchesToday;
